import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

// RTV Record — Return-to-Vendor tracker. Rows are created automatically
// from mail (backend rtv-mail-sync-timer / rtv-mail-backfill, one row per
// mail, deduped across sandeep@/rafique@/info@); this page lists them,
// lets edit-access users move Status along / correct fields / attach
// supporting documents, and exports Excel + an attachments zip. Visual
// language is GiftingDispatch.jsx's (same --gd-* tokens / dark hero).

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getUTCDate()).padStart(2, '0')} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function isoDay(value) { return value ? String(value).slice(0, 10) : ''; }

const PARTIES = ['Blinkit', 'Zepto', 'Nykaa', 'Swiggy', "Dorabjee's"];
const TYPES = ['RTV', 'Purchase Return', 'Debit Note', 'Rejection', 'Near Expiry-Damage', 'Purchase Invoice', 'Other'];
const STATUSES = ['Pickup Requested', 'Pickup Scheduled', 'Picked Up / In Transit', 'Received at Warehouse', 'Disputed', 'Cancelled', 'KAM Approval Pending', 'Disposed'];
const OPEN_STATUSES = new Set(['Pickup Requested', 'Pickup Scheduled', 'Picked Up / In Transit', 'Disputed', 'KAM Approval Pending']);
const STATUS_TONE = {
    'Pickup Requested':       { bg: '#EEE9F8', fg: '#6E58A6' },
    'Pickup Scheduled':       { bg: '#FFF2DC', fg: '#A26A12' },
    'Picked Up / In Transit': { bg: '#E3EEF9', fg: '#3B6EA5' },
    'Received at Warehouse':  { bg: '#E7F0E8', fg: '#5C7E60' },
    'Disputed':               { bg: '#F7E5EA', fg: '#C25E77' },
    'Cancelled':              { bg: '#F2F2F2', fg: '#9A9A9A' },
    'KAM Approval Pending':   { bg: '#FDF0E1', fg: '#B0553F' },
    'Disposed':               { bg: '#ECECEC', fg: '#5F5F5F' },
};

const HEADERS = ['Entry ID', 'Type', 'Goods Category', 'Request Date', 'Requested By', 'No. of Boxes', 'Pickup / Return Date', 'Courier Partner', 'Reference No(s)', 'Supporting Documents', 'Status', 'Discrepancy', 'Subject Line', ''];
const COLS = '0.85fr 1fr 1.3fr 0.95fr 1.2fr 0.7fr 0.95fr 0.95fr 1.4fr 1.6fr 1.35fr 1.3fr 2.2fr 0.6fr';

const isImage = a => /^image\//i.test(a.content_type || '') || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.file_name || '');

export default function RTVRecord({ user }) {
    const canEdit = hasEditAccess(user, 'po_so_lifecycle', 'rtv_record');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [partyFilter, setPartyFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    const [preview, setPreview] = useState(null);       // { url, file_name }
    const [editing, setEditing] = useState(null);       // row being edited
    const [zipping, setZipping] = useState(false);

    function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams({ action: 'list' });
            if (fromDate) qs.set('date_from', fromDate);
            if (toDate) qs.set('date_to', toDate);
            const res = await fetch(`${API_BASE}/rtv-record?${qs}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load');
            setRows(data.rows || []);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }, [fromDate, toDate]);

    useEffect(() => { loadRows(); }, [loadRows]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r =>
            (!partyFilter || r.party === partyFilter) &&
            (!typeFilter || r.type === typeFilter) &&
            (!statusFilter || r.status === statusFilter) &&
            (!q || [r.entry_id, r.subject, r.reference_numbers, r.goods_category, r.requested_by, r.courier_partner, r.discrepancy, r.remarks]
                .some(v => String(v || '').toLowerCase().includes(q)))
        );
    }, [rows, partyFilter, typeFilter, statusFilter, search]);

    const summary = useMemo(() => {
        const open = filteredRows.filter(r => OPEN_STATUSES.has(r.status));
        const byParty = PARTIES.map(p => ({ party: p, n: filteredRows.filter(r => r.party === p).length }));
        return {
            total: filteredRows.length,
            open: open.length,
            boxesPending: open.reduce((s, r) => s + (r.boxes_requested || 0), 0),
            disputed: filteredRows.filter(r => r.status === 'Disputed' || r.discrepancy).length,
            byParty,
        };
    }, [filteredRows]);

    async function saveFields(row, fields) {
        if (!canEdit) return notifyEditBlocked();
        try {
            const res = await fetch(`${API_BASE}/rtv-record?action=update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: row.id, username: user?.username, fields }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...fields, updated_by: user?.username } : r));
            showToast(`✓ ${row.entry_id || 'Record'} updated`);
            return true;
        } catch (err) {
            showToast(`✗ ${err.message}`);
            return false;
        }
    }

    async function openAttachment(att) {
        try {
            const res = await fetch(`${API_BASE}/rtv-record?action=attachment&id=${att.id}`);
            const data = await res.json();
            if (!res.ok || !data.url) throw new Error(data.error || 'Could not open file');
            if (isImage(att)) setPreview({ url: data.url, file_name: att.file_name });
            else window.open(data.url, '_blank', 'noopener');
        } catch (err) {
            showToast(`✗ ${err.message}`);
        }
    }

    function downloadExcel() {
        if (!window.XLSX || filteredRows.length === 0) return;
        const data = filteredRows.map(r => ({
            'Entry ID': r.entry_id || '',
            'Type': r.type,
            'Goods Category': r.goods_category || '',
            'Request Date': formatDate(r.request_date),
            'Requested By': r.requested_by || r.party,
            'Party': r.party,
            'No. of Boxes': r.boxes_requested ?? '',
            'Pickup / Return Date': formatDate(r.pickup_return_date),
            'Courier Partner': r.courier_partner || '',
            'Reference No(s)': r.reference_numbers || '',
            'Supporting Documents': (r.attachments || []).map(a => a.file_name).join(', '),
            'Status': r.status,
            'Discrepancy': r.discrepancy || '',
            'Subject Line': r.subject || '',
            'Sender': r.sender_email || '',
            'Mailbox': r.mailbox || '',
            'Received At': r.received_at ? new Date(r.received_at).toLocaleString('en-IN') : '',
            'Remarks': r.remarks || '',
            'Captured By': r.extraction_method || '',
            'Last Updated By': r.updated_by || '',
        }));
        const ws = window.XLSX.utils.json_to_sheet(data);
        ws['!cols'] = Object.keys(data[0]).map(k => ({ wch: k === 'Subject Line' ? 60 : Math.max(12, Math.min(34, k.length + 8)) }));
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, 'RTV Record');
        window.XLSX.writeFile(wb, `RTV_Record_${fromDate || 'all'}_to_${toDate || 'all'}.xlsx`);
    }

    async function downloadAttachments() {
        const ids = filteredRows.filter(r => (r.attachments || []).length).map(r => r.id);
        if (ids.length === 0) return showToast('No attachments in the current view');
        setZipping(true);
        try {
            const res = await fetch(`${API_BASE}/rtv-record?action=download_attachments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Download failed');
            }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `RTV_Attachments_${fromDate || 'all'}_to_${toDate || 'all'}.zip`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            showToast(`✗ ${err.message}`);
        }
        setZipping(false);
    }

    return (
        <div style={S.page}>
            <style>{EMBEDDED_CSS}</style>
            {toast && <div style={S.toast}>{toast}</div>}

            {/* ── Hero header — greyed drifting parcel/return motifs ───────── */}
            <div style={S.hero}>
                <div className="gd-hero-bg">
                    <span className="gd-float gd-float-1">📦</span>
                    <span className="gd-float gd-float-2">↩️</span>
                    <span className="gd-float gd-float-3">🚚</span>
                    <span className="gd-float gd-float-4">🏷️</span>
                    <span className="gd-float gd-float-5">📦</span>
                    <span className="gd-float gd-float-6">🔁</span>
                </div>
                <div style={S.heroContent}>
                    <div>
                        <div style={S.kicker}>PO &amp; SO Lifecycle</div>
                        <h1 style={S.title}>Return to Vendor — <b>RTV Record</b></h1>
                        <p style={S.subtitle}>Every RTV, purchase return, debit note and purchase invoice from Blinkit, Zepto, Nykaa, Swiggy and Dorabjee's, captured from mail with its documents.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button className="gd-btn" style={S.downloadBtn} onClick={downloadExcel} disabled={filteredRows.length === 0}>⬇ Download Excel</button>
                        <button className="gd-btn" style={S.attachBtn} onClick={downloadAttachments} disabled={zipping || filteredRows.length === 0}>
                            {zipping ? 'Preparing zip…' : '📎 Download Attachments'}
                        </button>
                    </div>
                </div>

                <div style={S.heroControlsRow}>
                    <input className="gd-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                    <span style={{ color: '#9A90AE', fontSize: 12.5, flexShrink: 0 }}>to</span>
                    <input className="gd-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={partyFilter} onChange={e => setPartyFilter(e.target.value)}>
                        <option value="">All Parties</option>
                        {PARTIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                        <option value="">All Types</option>
                        {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                        <option value="">All Statuses</option>
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input className="gd-hero-input" style={{ ...S.heroInput, flex: 1, minWidth: 120 }} placeholder="🔍 Search subject, reference, category…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <p style={S.heroShowingText}>Showing <b style={{ color: '#fff' }}>{filteredRows.length}</b> of {rows.length} records</p>
            </div>

            {/* ── KPI tiles ─────────────────────────────────────────────────── */}
            <div style={S.kpiRow}>
                <KpiTile label="Total Records" value={summary.total} />
                <KpiTile label="Open RTVs" value={summary.open} accent />
                <KpiTile label="Boxes Pending" value={summary.boxesPending} />
                <KpiTile label="Disputed / Discrepancy" value={summary.disputed} danger />
            </div>
            <div style={S.partyRow}>
                {summary.byParty.map(p => (
                    <button key={p.party} className="gd-btn" onClick={() => setPartyFilter(f => f === p.party ? '' : p.party)}
                        style={{ ...S.partyChip, ...(partyFilter === p.party ? S.partyChipActive : {}) }}>
                        {p.party} <b>{p.n}</b>
                    </button>
                ))}
            </div>

            {/* ── Table ─────────────────────────────────────────────────────── */}
            {loading ? (
                <p style={S.emptyState}>Loading…</p>
            ) : error ? (
                <p style={{ ...S.emptyState, color: 'var(--gd-error)' }}>Failed to load: {error}</p>
            ) : filteredRows.length === 0 ? (
                <p style={S.emptyState}>No RTV records in this view.</p>
            ) : (
                <div style={S.card} data-tutorial="rtv-table">
                    <div style={S.scrollWrap}>
                        <div style={{ minWidth: 1780 }}>
                            <div style={{ ...S.gridRow, ...S.gridHeader, gridTemplateColumns: COLS }}>
                                {HEADERS.map((h, i) => <div key={i} style={S.headCell}>{h}</div>)}
                            </div>
                            {filteredRows.map(r => (
                                <div key={r.id} className="gd-row" style={{ ...S.gridRow, gridTemplateColumns: COLS }}>
                                    <div style={{ ...S.cell, fontWeight: 700, color: 'var(--gd-purple)' }}>{r.entry_id || '—'}</div>
                                    <div style={S.cell}><span style={S.typeTag}>{r.type}</span></div>
                                    <div style={S.cell} title={r.goods_category || ''}>{r.goods_category || '—'}</div>
                                    <div style={S.cell}>{formatDate(r.request_date)}</div>
                                    <div style={S.cell} title={r.requested_by || ''}>{r.requested_by || r.party}</div>
                                    <div style={{ ...S.cell, ...S.numTab, fontWeight: 700 }}>{r.boxes_requested ?? '—'}</div>
                                    <div style={S.cell}>{formatDate(r.pickup_return_date)}</div>
                                    <div style={S.cell}>{r.courier_partner || '—'}</div>
                                    <div style={S.cell} title={r.reference_numbers || ''}>{r.reference_numbers || '—'}</div>
                                    <div style={{ ...S.cell, whiteSpace: 'normal', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                        {(r.attachments || []).length === 0 ? '—' : r.attachments.map(a => (
                                            <button key={a.id} className="gd-btn" style={S.docChip} title={a.file_name} onClick={() => openAttachment(a)}>
                                                {isImage(a) ? '🖼️' : /\.pdf$/i.test(a.file_name) ? '📄' : /\.(csv|xlsx?)$/i.test(a.file_name) ? '📊' : '📎'} {a.file_name.length > 18 ? a.file_name.slice(0, 16) + '…' : a.file_name}
                                            </button>
                                        ))}
                                    </div>
                                    <div style={S.cell}>
                                        {canEdit ? (
                                            <select className="gd-input" style={{ ...S.statusSelect, background: STATUS_TONE[r.status]?.bg, color: STATUS_TONE[r.status]?.fg }}
                                                value={r.status} onChange={e => saveFields(r, { status: e.target.value })}>
                                                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        ) : (
                                            <span style={{ ...S.statusBadge, background: STATUS_TONE[r.status]?.bg, color: STATUS_TONE[r.status]?.fg }}
                                                onClick={() => notifyEditBlocked()}>{r.status}</span>
                                        )}
                                    </div>
                                    <div style={{ ...S.cell, color: r.discrepancy ? 'var(--gd-error)' : undefined }} title={r.discrepancy || ''}>{r.discrepancy || '—'}</div>
                                    <div style={S.cell} title={r.subject || ''}>{r.subject || '—'}</div>
                                    <div style={{ ...S.cell, display: 'flex', gap: 4 }}>
                                        <button className="gd-btn" style={S.iconBtn} title="Edit / add documents" onClick={() => canEdit ? setEditing(r) : notifyEditBlocked()}>✎</button>
                                        {r.outlook_weblink && <a style={S.iconBtn} href={r.outlook_weblink} target="_blank" rel="noopener noreferrer" title="Open mail in Outlook">↗</a>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {preview && (
                <div style={S.modalOverlay} onClick={() => setPreview(null)}>
                    <div style={{ ...S.modal, width: 900 }} onClick={e => e.stopPropagation()}>
                        <div style={S.modalHeader}>
                            <b style={{ fontSize: 14 }}>{preview.file_name}</b>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <a className="gd-btn" style={S.cancelBtn} href={preview.url} target="_blank" rel="noopener noreferrer">Open original</a>
                                <button style={S.closeBtn} onClick={() => setPreview(null)}>✕</button>
                            </div>
                        </div>
                        <div style={{ ...S.modalBody, textAlign: 'center', background: '#F6F4FA' }}>
                            <img src={preview.url} alt={preview.file_name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <EditModal row={editing} user={user} onClose={() => setEditing(null)}
                    onSave={async fields => { if (await saveFields(editing, fields)) setEditing(null); }}
                    onUploaded={() => { showToast('✓ Documents uploaded'); loadRows(); }} />
            )}
        </div>
    );
}

function EditModal({ row, user, onClose, onSave, onUploaded }) {
    const [form, setForm] = useState({
        type: row.type || '',
        goods_category: row.goods_category || '',
        request_date: isoDay(row.request_date),
        requested_by: row.requested_by || '',
        boxes_requested: row.boxes_requested ?? '',
        pickup_return_date: isoDay(row.pickup_return_date),
        courier_partner: row.courier_partner || '',
        reference_numbers: row.reference_numbers || '',
        status: row.status,
        discrepancy: row.discrepancy || '',
        remarks: row.remarks || '',
    });
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    async function upload() {
        if (!files.length) return;
        setBusy(true);
        setErr(null);
        try {
            const fd = new FormData();
            fd.append('rtv_id', row.id);
            fd.append('username', user?.username || '');
            files.forEach(f => fd.append('files', f));
            const res = await fetch(`${API_BASE}/rtv-record?action=upload`, { method: 'POST', body: fd });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Upload failed');
            setFiles([]);
            onUploaded();
        } catch (e) {
            setErr(e.message);
        }
        setBusy(false);
    }

    const field = (label, key, type = 'text') => (
        <div>
            <div style={S.fieldLabel}>{label}</div>
            <input className="gd-input" type={type} value={form[key]} onChange={e => set(key, e.target.value)} />
        </div>
    );

    return (
        <div style={S.modalOverlay} onClick={onClose}>
            <div style={S.modal} onClick={e => e.stopPropagation()}>
                <div style={S.modalHeader}>
                    <div>
                        <b style={{ fontSize: 15 }}>{row.entry_id || 'RTV record'} · {row.party}</b>
                        <div style={{ fontSize: 12, color: 'var(--gd-text-faint)', marginTop: 2 }}>{row.subject}</div>
                    </div>
                    <button style={S.closeBtn} onClick={onClose}>✕</button>
                </div>
                <div style={S.modalBody}>
                    {err && <div style={S.errorBanner}>{err}</div>}
                    <div style={S.formGrid}>
                        <div>
                            <div style={S.fieldLabel}>Type</div>
                            <select className="gd-input" value={form.type} onChange={e => set('type', e.target.value)}>
                                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <div style={S.fieldLabel}>Status</div>
                            <select className="gd-input" value={form.status} onChange={e => set('status', e.target.value)}>
                                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        {field('Goods Category', 'goods_category')}
                        {field('Requested By', 'requested_by')}
                        {field('Request Date', 'request_date', 'date')}
                        {field('Pickup / Return Date (deadline)', 'pickup_return_date', 'date')}
                        {field('No. of Boxes', 'boxes_requested', 'number')}
                        {field('Courier Partner', 'courier_partner')}
                    </div>
                    {field('Reference No(s)', 'reference_numbers')}
                    <div style={{ height: 12 }} />
                    {field('Discrepancy', 'discrepancy')}
                    <div style={{ height: 12 }} />
                    {field('Remarks', 'remarks')}

                    <div style={S.sectionTitle}>Supporting documents</div>
                    <div style={{ fontSize: 12.5, color: 'var(--gd-text-muted)', marginBottom: 8 }}>
                        {(row.attachments || []).length ? row.attachments.map(a => a.file_name).join(', ') : 'None yet.'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="file" multiple accept="image/*,.pdf,.csv,.xlsx,.xls,.doc,.docx" onChange={e => setFiles([...e.target.files])} />
                        <button className="gd-btn" style={S.cancelBtn} disabled={!files.length || busy} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--gd-text-faint)', marginTop: 6 }}>Delivery challan, WhatsApp images, PoD, etc.</div>
                </div>
                <div style={S.modalFooter}>
                    <button className="gd-btn" style={S.cancelBtn} onClick={onClose}>Cancel</button>
                    <button className="gd-btn" style={S.saveBtn} onClick={() => onSave(form)}>Save</button>
                </div>
            </div>
        </div>
    );
}

function KpiTile({ label, value, accent, danger }) {
    return (
        <div style={S.kpiTile}>
            <div style={{ fontSize: 26, fontWeight: 700, color: danger ? 'var(--gd-error)' : accent ? 'var(--gd-purple)' : 'var(--gd-text)', fontVariantNumeric: 'tabular-nums' }}>
                {Number(value || 0).toLocaleString('en-IN')}
            </div>
            <div style={S.kpiLabel}>{label}</div>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/* Styles — same --gd-* token set as GiftingDispatch.jsx / GRN Management. */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
:root {
  --gd-surface: #fff; --gd-surface-subtle: #FBFAFD;
  --gd-border: #ECE7F2; --gd-border-soft: #E4DEEE;
  --gd-text: #231C30; --gd-text-muted: #8C82A0; --gd-text-faint: #9A90AE;
  --gd-accent-surface: #231C30;
  --gd-purple: #6E58A6; --gd-purple-tint: #EEE9F8;
  --gd-green: #5C7E60; --gd-green-tint: #E7F0E8;
  --gd-error: #C25E77; --gd-error-tint: #F7E5EA;
  --gd-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --gd-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
}
.gd-btn { font-family: 'Space Grotesk', sans-serif; transition: transform 0.15s ease, background 0.2s ease; cursor: pointer; }
.gd-btn:active:not(:disabled) { transform: scale(0.96); }
.gd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gd-row:nth-child(even) { background: var(--gd-surface-subtle); }
.gd-row:hover { background: var(--gd-purple-tint); }
.gd-input { font-family: 'Space Grotesk', sans-serif; padding: 10px 14px; border-radius: 10px; border: 1px solid var(--gd-border-soft); background: #fff; color: var(--gd-text); font-size: 13.5px; width: 100%; box-sizing: border-box; }
.gd-input:focus { border-color: var(--gd-purple) !important; box-shadow: 0 0 0 3px rgba(110,88,166,0.18); outline: none; }
.gd-hero-input:focus { border-color: #A48FD1 !important; box-shadow: 0 0 0 3px rgba(164,143,209,0.25) !important; outline: none; background: #2A2140 !important; color: #fff !important; }
select.gd-hero-input { color-scheme: dark; }
.gd-hero-input option { background: #2A2140; color: #fff; }
.gd-hero-input option:checked { background: #6E58A6; color: #fff; }
.gd-hero-input::placeholder { color: #9A90AE; }
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }

.gd-hero-bg { position: absolute; inset: 0; overflow: hidden; border-radius: 20px; pointer-events: none; }
/* Greyed-out parcel/return motifs: grayscale + low opacity. */
.gd-float { position: absolute; font-size: 36px; opacity: 0.14; filter: grayscale(1) drop-shadow(0 4px 10px rgba(0,0,0,0.25)); animation: gd-drift 9s ease-in-out infinite; }
.gd-float-1 { top: 12%; left: 6%;  animation-duration: 8s; }
.gd-float-2 { top: 60%; left: 14%; animation-duration: 10s; animation-delay: 1s; }
.gd-float-3 { top: 20%; left: 88%; animation-duration: 7.5s; animation-delay: 0.5s; }
.gd-float-4 { top: 70%; left: 80%; animation-duration: 9.5s; animation-delay: 2s; }
.gd-float-5 { top: 40%; left: 48%; animation-duration: 11s; animation-delay: 1.5s; }
.gd-float-6 { top: 15%; left: 62%; animation-duration: 8.5s; animation-delay: 0.8s; }
@keyframes gd-drift {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-16px) rotate(-8deg); }
}
`;

const S = {
    page: { padding: 28, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--gd-text)' },
    toast: { position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', background: '#231C30', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 13, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
    hero: { position: 'relative', background: 'linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%)', border: '1px solid #2A2338', borderRadius: 20, padding: '32px 32px', marginBottom: 20, overflow: 'hidden', boxShadow: '0 10px 30px rgba(20,16,31,0.3)' },
    heroContent: { position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' },
    kicker: { fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A48FD1' },
    title: { fontSize: 30, fontWeight: 500, margin: '6px 0 10px', color: '#fff', letterSpacing: '-0.01em' },
    subtitle: { fontSize: 14.5, color: '#B2AAC4', margin: 0, maxWidth: 560, lineHeight: 1.55 },
    downloadBtn: { padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--gd-green)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    attachBtn: { padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--gd-purple)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    heroControlsRow: { position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center', marginTop: 22, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.12)', overflowX: 'auto' },
    heroInput: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroSelect: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: '#2A2140', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroShowingText: { position: 'relative', zIndex: 1, fontSize: 12.5, color: '#9A90AE', margin: '12px 0 0' },

    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 12 },
    kpiTile: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 14, padding: '16px 18px', boxShadow: 'var(--gd-shadow-sm)' },
    kpiLabel: { fontSize: 11, color: 'var(--gd-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 6, fontWeight: 600 },
    partyRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
    partyChip: { padding: '7px 14px', borderRadius: 999, border: '1px solid var(--gd-border-soft)', background: '#fff', color: 'var(--gd-text)', fontSize: 12.5, display: 'flex', gap: 6 },
    partyChipActive: { background: 'var(--gd-purple)', borderColor: 'var(--gd-purple)', color: '#fff' },

    emptyState: { padding: 48, textAlign: 'center', color: 'var(--gd-text-faint)', fontSize: 14 },
    card: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--gd-shadow-md)' },
    scrollWrap: { overflow: 'auto', maxHeight: '68vh' },
    gridRow: { display: 'grid', alignItems: 'center', borderBottom: '1px solid var(--gd-border)' },
    gridHeader: { position: 'sticky', top: 0, background: 'var(--gd-accent-surface)', color: '#fff', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', zIndex: 1 },
    headCell: { padding: '13px 10px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
    cell: { padding: '11px 10px', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    numTab: { fontVariantNumeric: 'tabular-nums' },
    typeTag: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: 'var(--gd-purple-tint)', color: 'var(--gd-purple)' },
    docChip: { fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--gd-border-soft)', background: '#fff', color: 'var(--gd-text)', whiteSpace: 'nowrap' },
    statusSelect: { padding: '5px 8px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none' },
    statusBadge: { display: 'inline-block', fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'not-allowed' },
    iconBtn: { width: 28, height: 28, borderRadius: 8, border: '1px solid var(--gd-border-soft)', background: '#fff', color: 'var(--gd-purple)', fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: 'var(--gd-surface)', borderRadius: 18, width: 720, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--gd-border)' },
    closeBtn: { background: 'var(--gd-surface-subtle)', border: 'none', borderRadius: 8, width: 30, height: 30, fontSize: 14, cursor: 'pointer', color: 'var(--gd-text-muted)' },
    modalBody: { padding: '18px 22px', overflowY: 'auto', flex: 1 },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },
    fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--gd-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--gd-purple)', margin: '18px 0 10px', paddingTop: 12, borderTop: '1px solid var(--gd-border)' },
    errorBanner: { background: 'var(--gd-error-tint)', color: 'var(--gd-error)', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14, fontWeight: 600 },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', borderTop: '1px solid var(--gd-border)' },
    cancelBtn: { padding: '11px 20px', borderRadius: 10, border: '1px solid var(--gd-border-soft)', background: '#fff', color: 'var(--gd-text)', fontWeight: 600, fontSize: 13.5, textDecoration: 'none' },
    saveBtn: { padding: '11px 24px', borderRadius: 10, border: 'none', background: 'var(--gd-purple)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
};
